import * as React from "react";
import {
	alpha,
	Avatar,
	Box,
	ButtonBase,
	CircularProgress,
	Dialog,
	DialogContent,
	Fade,
	IconButton,
	Skeleton,
	Stack,
	Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import {makeStyles} from "tss-react/mui";
import {useMq} from "@buildwitt/shapeshift-common/v1/hooks";
import {useQueryClient} from "@tanstack/react-query";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {
	LEADERBOARD_MODAL_PAGE_SIZE,
	LEADERBOARDS_QUERY_KEYS,
	useListCohortLeaderboardInfinite,
	type CohortLeaderboardEntryDto
} from "@/app2/shared/data";
import {LeaderboardPointsGuideModal} from "./LeaderboardPointsGuideModal";
import {formatLeaderboardRank, getLeaderboardTrophyPublicPath} from "./leaderboardUiUtils";

const useStyles = makeStyles<{isMobile: boolean}>()((theme, {isMobile}) => {
	const rowPaddingY = theme.spacing(2);
	const rowPaddingX = isMobile ? theme.spacing(2) : theme.spacing(3);
	const dialogPadding = isMobile ? theme.spacing(2) : theme.spacing(4);
	const selectedOpacity =
		typeof theme.palette.action.selectedOpacity === "number"
			? theme.palette.action.selectedOpacity
			: theme.palette.action.hoverOpacity;
	const overlineTypo = theme.typography.overline ?? theme.typography.caption;
	const subtitleTypo = theme.typography.subtitle1;
	const body2Typo = theme.typography.body2;
	const h6Typo = theme.typography.h6;
	const titleStripPad = theme.spacing(2);

	return {
		dialogBackdrop: {
			backgroundColor: alpha(theme.palette.common.black, theme.palette.action.disabledOpacity)
		},
		dialogPaper: {
			backgroundColor: theme.palette.background.paperElevation2,
			borderRadius: theme.spacing(2),
			color: theme.palette.text.primary,
			marginTop: isMobile ? 0 : theme.spacing(7.625),
			maxWidth: isMobile ? "100%" : theme.spacing(90),
			width: "100%",
			boxSizing: "border-box",
			position: "relative",
			overflow: "hidden",
			border: `${theme.spacing(0.125)} solid ${theme.palette.divider}`
		},
		closeButtonInStrip: {
			flexShrink: 0,
			alignSelf: "center",
			color: theme.palette.text.primary
		},
		contentRoot: {
			paddingLeft: dialogPadding,
			paddingRight: dialogPadding,
			paddingBottom: dialogPadding,
			paddingTop: 0,
			"&:first-of-type": {
				paddingTop: 0
			}
		},
		/** Matches `App2AnnouncementModal` `dialogTitle` (DialogTitle + h6). */
		companyTitle: {
			padding: 0,
			fontFamily: theme.typography.fontFamily,
			fontSize: "24px",
			lineHeight: h6Typo.lineHeight,
			fontWeight: h6Typo.fontWeight,
			color: theme.palette.text.primary,
			textAlign: "left",
			width: "100%",
			margin: 0
		},
		titleCloseStripGrow: {
			flex: "1 1 auto",
			minWidth: 0,
			display: "flex",
			alignItems: "center",
			alignSelf: "stretch"
		},
		titleCloseStrip: {
			display: "flex",
			flexDirection: "row",
			alignItems: "center",
			justifyContent: "flex-start",
			gap: theme.spacing(1),
			width: "100%",
			boxSizing: "border-box",
			minHeight: 0,
			paddingTop: titleStripPad,
			paddingBottom: titleStripPad
		},
		headerRowOuter: {
			width: "100%",
			display: "flex",
			justifyContent: "flex-start",
			alignItems: "center",
			marginBottom: theme.spacing(3)
		},
		headerRow: {
			display: "flex",
			flexDirection: "row",
			alignItems: "center",
			gap: 0,
			maxWidth: "100%",
			textAlign: "left",
			[theme.breakpoints.down("sm")]: {
				alignItems: "flex-start"
			}
		},
		headerTextStack: {
			flex: "0 1 auto",
			minWidth: 0,
			display: "flex",
			flexDirection: "column",
			alignItems: "flex-start",
			[theme.breakpoints.down("sm")]: {
				alignItems: "stretch",
				paddingTop: theme.spacing(0.5)
			}
		},
		/** Matches `LeaderboardCard` trophy slot (public SVG under `/leaderboard/`). */
		trophyWrap: {
			display: "flex",
			alignItems: "center",
			justifyContent: "flex-start",
			flexShrink: 0,
			width: "157px",
			height: "159px",
			boxSizing: "border-box",
			margin: "-30px"
		},
		trophyImg: {
			width: "100%",
			height: "100%",
			display: "block",
			objectFit: "contain"
		},
		overlineLabel: {
			fontFamily: theme.typography.fontFamily,
			fontSize: overlineTypo.fontSize,
			lineHeight: overlineTypo.lineHeight,
			letterSpacing: overlineTypo.letterSpacing ?? "0.12em",
			textTransform: overlineTypo.textTransform ?? "uppercase",
			color: theme.palette.text.secondary,
			marginTop: 0,
			marginBottom: 0,
			fontWeight: theme.typography.fontWeightMedium,
			textAlign: "left",
			width: "100%"
		},
		headline: {
			fontFamily: theme.typography.fontFamily,
			fontSize: "34px",
			marginBottom: 0,
			lineHeight: 1.2,
			textAlign: "left",
			width: "100%"
		},
		headlineRank: {
			color: theme.palette.primary.main
		},
		headlineRest: {
			color: theme.palette.text.primary
		},
		pointsGuideLink: {
			color: theme.palette.text.secondary,
			fontFamily: theme.typography.fontFamily,
			fontSize: "16px",
			lineHeight: body2Typo.lineHeight,
			fontWeight: theme.typography.fontWeightMedium,
			textDecoration: "underline",
			textUnderlineOffset: theme.spacing(0.25),
			borderRadius: theme.shape.borderRadius,
			textAlign: "left",
			alignSelf: "flex-start"
		},
		headerContentDivider: {
			width: "100%",
			height: 0,
			margin: 0,
			marginTop: 0,
			marginBottom: theme.spacing(2),
			border: "none",
			borderTop: `${theme.spacing(0.125)} solid ${theme.palette.divider}`,
			flexShrink: 0
		},
		tableRegion: {
			marginTop: 0,
			display: "flex",
			flexDirection: "column",
			minHeight: 0
		},
		listScrollArea: {
			maxHeight: isMobile ? "min(52vh, 420px)" : theme.spacing(56),
			overflowY: "auto",
			overflowX: "hidden",
			overscrollBehavior: "contain",
			WebkitOverflowScrolling: "touch"
		},
		loadMoreSentinel: {
			height: 1,
			width: "100%",
			flexShrink: 0
		},
		columnHeaderRow: {
			display: "grid",
			gridTemplateColumns: `${theme.spacing(8)} minmax(0, 1fr) ${theme.spacing(10)}`,
			alignItems: "center",
			columnGap: theme.spacing(1),
			paddingLeft: rowPaddingX,
			paddingRight: rowPaddingX,
			paddingBottom: theme.spacing(1.5),
			borderBottom: `${theme.spacing(0.125)} solid ${theme.palette.divider}`
		},
		columnHeaderText: {
			fontFamily: theme.typography.fontFamily,
			fontSize: overlineTypo.fontSize,
			lineHeight: overlineTypo.lineHeight,
			letterSpacing: overlineTypo.letterSpacing ?? "0.08em",
			textTransform: overlineTypo.textTransform ?? "uppercase",
			color: theme.palette.text.secondary,
			fontWeight: theme.typography.fontWeightMedium
		},
		columnHeaderPoints: {
			textAlign: "right"
		},
		list: {
			listStyle: "none",
			margin: 0,
			padding: 0
		},
		listItemBase: {
			display: "grid",
			gridTemplateColumns: `${theme.spacing(8)} minmax(0, 1fr) ${theme.spacing(10)}`,
			alignItems: "center",
			columnGap: theme.spacing(1),
			padding: `${rowPaddingY} ${rowPaddingX}`
		},
		listRowDivider: {
			borderBottom: `${theme.spacing(0.125)} solid ${theme.palette.divider}`
		},
		listRowDividerNone: {
			borderBottom: "none"
		},
		listRowCurrent: {
			borderRadius: theme.spacing(1),
			border: `${theme.spacing(0.25)} solid ${theme.palette.primary.main}`,
			backgroundColor: alpha(theme.palette.primary.main, selectedOpacity),
			marginTop: theme.spacing(0.5),
			marginBottom: theme.spacing(0.5)
		},
		rankCell: {
			display: "flex",
			alignItems: "center",
			gap: theme.spacing(0.25)
		},
		rankNumber: {
			fontFamily: theme.typography.fontFamily,
			fontSize: "11px",
			lineHeight: subtitleTypo.lineHeight,
			fontWeight: theme.typography.fontWeightBold,
			fontVariantNumeric: "tabular-nums",
			color: theme.palette.text.secondary
		},
		rankNumberCurrent: {
			color: theme.palette.primary.main
		},
		trendIcon: {
			fontSize: "34px",
			lineHeight: 1,
			display: "flex"
		},
		trendUp: {
			color: theme.palette.success.main
		},
		trendDown: {
			color: theme.palette.error.main
		},
		nameStack: {
			minWidth: 0,
			display: "flex",
			flexDirection: "column",
			alignItems: "flex-start",
			gap: theme.spacing(0.25),
			textAlign: "left",
			width: "100%"
		},
		namePrimary: {
			fontFamily: theme.typography.fontFamily,
			fontSize: subtitleTypo.fontSize,
			lineHeight: subtitleTypo.lineHeight,
			fontWeight: theme.typography.fontWeightBold,
			color: theme.palette.text.primary,
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap",
			textAlign: "left",
			width: "100%"
		},
		nameRole: {
			fontFamily: theme.typography.fontFamily,
			fontSize: body2Typo.fontSize,
			lineHeight: body2Typo.lineHeight,
			fontWeight: body2Typo.fontWeight,
			color: theme.palette.text.secondary,
			overflow: "hidden",
			textOverflow: "ellipsis",
			whiteSpace: "nowrap",
			textAlign: "left",
			width: "100%"
		},
		points: {
			fontFamily: theme.typography.fontFamily,
			fontSize: subtitleTypo.fontSize,
			lineHeight: subtitleTypo.lineHeight,
			fontWeight: theme.typography.fontWeightBold,
			textAlign: "right",
			fontVariantNumeric: "tabular-nums",
			color: theme.palette.text.primary
		},
		pointsCurrent: {
			color: theme.palette.primary.main
		},
		avatar: {
			width: theme.spacing(5),
			height: theme.spacing(5),
			marginRight: theme.spacing(1.5),
			border: `${theme.spacing(0.125)} solid ${theme.palette.divider}`
		},
		nameRow: {
			display: "flex",
			alignItems: "center",
			justifyContent: "flex-start",
			minWidth: 0,
			width: "100%",
			textAlign: "left"
		}
	};
});

function trimUnknownToString(value: unknown): string {
	if (value == null || value === false) return "";
	if (typeof value === "string") return value.trim();
	return String(value).trim();
}

function cohortLeaderboardDisplayName(entry: CohortLeaderboardEntryDto): string {
	const first = trimUnknownToString(entry.firstName);
	const last = trimUnknownToString(entry.lastName);
	return [first, last].filter(Boolean).join(" ");
}

function cohortLeaderboardAvatarUrl(entry: CohortLeaderboardEntryDto): string | undefined {
	const s = trimUnknownToString(entry.profileImage);
	return s || undefined;
}

function cohortLeaderboardJobTitle(entry: CohortLeaderboardEntryDto): string {
	return trimUnknownToString(entry.jobTitle);
}

function cohortLeaderboardIsTrendPositive(entry: CohortLeaderboardEntryDto): boolean {
	/** OpenAPI types `isPositiveRank` loosely; runtime is boolean when `dataSource` is `leaderboard`. */
	const v = entry.isPositiveRank as unknown;
	return v === true;
}

function initialsFromName(name: string): string {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (parts.length === 0) return "?";
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return `${parts[0][0] ?? ""}${parts[parts.length - 1][0] ?? ""}`.toUpperCase();
}

function PositiveRankGlyph({
	isPositiveRank,
	trendIcon,
	trendUp,
	trendDown
}: {
	isPositiveRank: boolean;
	trendIcon: string;
	trendUp: string;
	trendDown: string;
}) {
	return isPositiveRank ? (
		<ArrowDropUpIcon className={`${trendIcon} ${trendUp}`} aria-hidden />
	) : (
		<ArrowDropDownIcon className={`${trendIcon} ${trendDown}`} aria-hidden />
	);
}

export type LeaderboardModalProps = {
	open: boolean;
	onClose: () => void;
	/** Shown as small uppercase label above the title (e.g. localized month + “ranking”). */
	monthRankingLabel: string;
	/** Numeric rank for the headline (e.g. 12 → “#12”). */
	titleRankNumber: number;
	/** Remainder of the headline after the rank (e.g. “on the board”). */
	titleSuffix: string;
	/** Cohort for `GET /api/_internal/leaderboards/:cohortId/cohort`. */
	cohortId: number;
	/** Highlights the learner’s row in the standings. */
	currentUserId: number;
};

export const LeaderboardModal: React.FC<LeaderboardModalProps> = ({
	open,
	onClose,
	monthRankingLabel,
	titleRankNumber,
	titleSuffix,
	cohortId,
	currentUserId
}) => {
	const mq = useMq();
	const {classes, cx} = useStyles({isMobile: mq.mobile});
	const {t} = useSSTranslation();
	const queryClient = useQueryClient();
	const [pointsGuideOpen, setPointsGuideOpen] = React.useState(false);
	const listScrollRef = React.useRef<HTMLDivElement>(null);
	const loadMoreRef = React.useRef<HTMLDivElement>(null);
	const currentUserRowRef = React.useRef<HTMLLIElement>(null);
	const hasScrolledToUser = React.useRef(false);

	React.useEffect(() => {
		if (!open) {
			setPointsGuideOpen(false);
			hasScrolledToUser.current = false;
		}
	}, [open]);

	React.useEffect(() => {
		if (!open || cohortId <= 0) {
			queryClient.removeQueries({
				queryKey: [LEADERBOARDS_QUERY_KEYS.COHORT_LEADERBOARD, "infinite", cohortId]
			});
		}
	}, [open, cohortId, queryClient]);

	const listEnabled = open && cohortId > 0;

	const seekPageSize = React.useMemo(
		() => Math.min(Math.max(titleRankNumber + 10, LEADERBOARD_MODAL_PAGE_SIZE), 200),
		[titleRankNumber]
	);

	const {
		data: cohortPages,
		isPending: isListPending,
		isFetching: isListFetching,
		isError: isListError,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage
	} = useListCohortLeaderboardInfinite(cohortId > 0 ? cohortId : undefined, {pageSize: seekPageSize}, {
		enabled: listEnabled
	});

	const isListLoading =
		listEnabled && cohortPages === undefined && !isListError && (isListPending || isListFetching);

	const items = React.useMemo(
		() => cohortPages?.pages.flatMap((page) => page.items) ?? [],
		[cohortPages?.pages]
	);

	React.useEffect(() => {
		if (!listEnabled || !hasNextPage || isFetchingNextPage) return;

		const root = listScrollRef.current;
		const target = loadMoreRef.current;
		if (!root || !target) return;

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					void fetchNextPage();
				}
			},
			{root, rootMargin: "120px", threshold: 0}
		);

		observer.observe(target);
		return () => observer.disconnect();
	}, [listEnabled, hasNextPage, isFetchingNextPage, fetchNextPage, items.length]);

	React.useEffect(() => {
		if (hasScrolledToUser.current || items.length === 0) return;
		const userInList = items.some((row) => row.userId === currentUserId);

		if (!userInList) {
			if (hasNextPage && !isFetchingNextPage) {
				void fetchNextPage();
			}
			return;
		}

		requestAnimationFrame(() => {
			const row = currentUserRowRef.current;
			const container = listScrollRef.current;
			if (row && container) {
				row.scrollIntoView({block: "center", behavior: "smooth"});
				hasScrolledToUser.current = true;
			}
		});
	}, [items, currentUserId, hasNextPage, isFetchingNextPage, fetchNextPage]);

	const isLocatingUser =
		!hasScrolledToUser.current &&
		items.length > 0 &&
		!items.some((row) => row.userId === currentUserId) &&
		hasNextPage;

	const trophySrc = React.useMemo(
		() => getLeaderboardTrophyPublicPath(titleRankNumber),
		[titleRankNumber]
	);

	function RowView({row, isLast}: {row: CohortLeaderboardEntryDto; isLast: boolean}) {
		const isCurrent = row.userId === currentUserId;
		const name = cohortLeaderboardDisplayName(row) || "\u2014";
		const roleText = cohortLeaderboardJobTitle(row);
		const rankClass = cx(classes.rankNumber, isCurrent && classes.rankNumberCurrent);
		const pointsClass = cx(classes.points, isCurrent && classes.pointsCurrent);
		const itemClass = cx(
			classes.listItemBase,
			isCurrent
				? classes.listRowCurrent
				: cx(classes.listRowDivider, isLast && classes.listRowDividerNone)
		);

		return (
			<Box
				component="li"
				ref={isCurrent ? currentUserRowRef : undefined}
				className={itemClass}
				aria-current={isCurrent ? "true" : undefined}
			>
				<Box className={classes.rankCell}>
					<Typography component="span" className={rankClass}>
						{formatLeaderboardRank(row.rank)}
					</Typography>
					<PositiveRankGlyph
						isPositiveRank={cohortLeaderboardIsTrendPositive(row)}
						trendIcon={classes.trendIcon}
						trendUp={classes.trendUp}
						trendDown={classes.trendDown}
					/>
				</Box>
				<Box className={classes.nameRow}>
					<Avatar
						className={classes.avatar}
						src={cohortLeaderboardAvatarUrl(row)}
						alt=""
					>
						{initialsFromName(name)}
					</Avatar>
					<Stack className={classes.nameStack}>
						<Typography component="span" className={classes.namePrimary}>
							{name}
						</Typography>
						<Typography component="span" className={classes.nameRole}>
							{roleText || "\u2014"}
						</Typography>
					</Stack>
				</Box>
				<Typography component="span" className={pointsClass}>
					{row.totalPoints.toLocaleString()}
				</Typography>
			</Box>
		);
	}

	const handleDialogClose = (_event: unknown, reason: "backdropClick" | "escapeKeyDown") => {
		if (reason === "backdropClick" || reason === "escapeKeyDown") {
			onClose();
		}
	};

	const rankCol = t("leaderboard_modal_col_rank", "Rank");
	const nameCol = t("leaderboard_modal_col_name", "Name");
	const pointsCol = t("leaderboard_modal_col_points", "Points");

	return (
		<>
			<Dialog
				open={open}
				fullScreen={mq.mobile}
				onClose={handleDialogClose}
				TransitionComponent={Fade}
				aria-labelledby="leaderboard-modal-company-title"
				aria-modal="true"
				data-test-id="leaderboard-modal"
				BackdropProps={{
					className: classes.dialogBackdrop
				}}
				PaperProps={{
					className: classes.dialogPaper
				}}
			>
				<DialogContent className={classes.contentRoot} data-test-id="leaderboard-modal-content">
					<Box className={classes.titleCloseStrip}>
						<Box className={classes.titleCloseStripGrow}>
							<Typography
								id="leaderboard-modal-company-title"
								className={classes.companyTitle}
								component="h2"
								variant="h6"
								data-test-id="leaderboard-modal-company-title"
							>
								{t("leaderboard_modal_company_title", "Company Leaderboard")}
							</Typography>
						</Box>
						<IconButton
							onClick={onClose}
							className={classes.closeButtonInStrip}
							aria-label="Close leaderboard dialog"
							data-test-id="leaderboard-modal-close-btn"
							edge="end"
							size="medium"
						>
							<CloseIcon />
						</IconButton>
					</Box>

					<Box className={classes.headerContentDivider} component="hr" aria-hidden />

					<Box className={classes.headerRowOuter}>
						<Box className={classes.headerRow}>
							<Box className={classes.trophyWrap}>
								<Box
									component="img"
									src={trophySrc}
									alt=""
									className={classes.trophyImg}
									aria-hidden
								/>
							</Box>
							<Stack className={classes.headerTextStack} spacing={0}>
								<Typography className={classes.overlineLabel} component="p">
									{monthRankingLabel}
								</Typography>
								<Typography
									id="leaderboard-dialog-title"
									className={classes.headline}
									component="h2"
									data-test-id="leaderboard-modal-title"
								>
									<Box component="span" className={classes.headlineRank}>
										#{formatLeaderboardRank(titleRankNumber)}
									</Box>{" "}
									<Box component="span" className={classes.headlineRest}>
										{titleSuffix}
									</Box>
								</Typography>
								<ButtonBase
									className={classes.pointsGuideLink}
									onClick={() => setPointsGuideOpen(true)}
									aria-haspopup="dialog"
									aria-expanded={pointsGuideOpen}
									data-test-id="leaderboard-modal-points-guide-link"
								>
									{t("leaderboard_points_guide_link", "See how we calculate each point")}
								</ButtonBase>
							</Stack>
						</Box>
					</Box>

					<Box
						className={classes.tableRegion}
						role="region"
						aria-label={t("leaderboard_modal_list_label", "Leaderboard standings")}
						data-test-id="leaderboard-modal-standings"
					>
						<Box component="div" className={classes.columnHeaderRow} aria-hidden>
							<Typography className={classes.columnHeaderText} variant="overline" component="span">
								{rankCol}
							</Typography>
							<Typography className={classes.columnHeaderText} variant="overline" component="span">
								{nameCol}
							</Typography>
							<Typography
								className={`${classes.columnHeaderText} ${classes.columnHeaderPoints}`}
								variant="overline"
								component="span"
							>
								{pointsCol}
							</Typography>
						</Box>

						{isListError ? (
							<Typography variant="body2" color="text.secondary" sx={{px: 0, py: 2}}>
								{t(
									"leaderboard_modal_list_error",
									"Could not load standings. Try closing and opening again."
								)}
							</Typography>
						) : isListLoading ? (
							<Box component="ul" className={classes.list} role="list" aria-busy="true">
								{Array.from({length: 5}).map((_, index) => (
									<Box
										key={index}
										component="li"
										className={cx(
											classes.listItemBase,
											classes.listRowDivider,
											index === 4 && classes.listRowDividerNone
										)}
									>
										<Skeleton variant="text" width={36} height={28} />
										<Box className={classes.nameRow}>
											<Skeleton
												variant="circular"
												width={40}
												height={40}
												sx={{mr: 1.5, flexShrink: 0}}
											/>
											<Stack spacing={0.5} sx={{flex: 1, minWidth: 0}}>
												<Skeleton variant="text" width="55%" height={24} />
												<Skeleton variant="text" width="40%" height={20} />
											</Stack>
										</Box>
										<Skeleton variant="text" width={48} height={24} sx={{justifySelf: "end"}} />
									</Box>
								))}
							</Box>
						) : items.length === 0 ? (
							<Typography variant="body2" color="text.secondary" sx={{py: 2}}>
								{t(
									"leaderboard_modal_list_empty",
									"No standings for this period yet."
								)}
							</Typography>
						) : (
							<Box
								ref={listScrollRef}
								className={classes.listScrollArea}
								data-test-id="leaderboard-modal-list-scroll"
								sx={{position: "relative"}}
							>
								{isLocatingUser ? (
									<Box
										sx={{
											position: "absolute",
											inset: 0,
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											justifyContent: "center",
											backgroundColor: (theme) =>
												alpha(theme.palette.background.paperElevation2, 0.85),
											zIndex: 2,
											gap: 1.5,
											borderRadius: 1
										}}
										aria-live="polite"
										data-test-id="leaderboard-modal-locating-user"
									>
										<CircularProgress size={32} />
										<Typography variant="body2" color="text.secondary">
											{t("leaderboard_modal_locating_user", "Finding your position…")}
										</Typography>
									</Box>
								) : null}
								<Box component="ul" className={classes.list} role="list">
									{items.map((row, index) => (
										<RowView
											key={row.userId}
											row={row}
											isLast={index === items.length - 1 && !hasNextPage}
										/>
									))}
									{isFetchingNextPage
										? Array.from({length: 3}).map((_, index) => (
											<Box
												key={`loading-${index}`}
												component="li"
												className={cx(
													classes.listItemBase,
													classes.listRowDivider,
													index === 2 && classes.listRowDividerNone
												)}
												aria-hidden
											>
												<Skeleton variant="text" width={36} height={28} />
												<Box className={classes.nameRow}>
													<Skeleton
														variant="circular"
														width={40}
														height={40}
														sx={{mr: 1.5, flexShrink: 0}}
													/>
													<Stack spacing={0.5} sx={{flex: 1, minWidth: 0}}>
														<Skeleton variant="text" width="55%" height={24} />
														<Skeleton variant="text" width="40%" height={20} />
													</Stack>
												</Box>
												<Skeleton
													variant="text"
													width={48}
													height={24}
													sx={{justifySelf: "end"}}
												/>
											</Box>
										))
										: null}
								</Box>
								{hasNextPage ? (
									<Box
										ref={loadMoreRef}
										className={classes.loadMoreSentinel}
										aria-hidden
										data-test-id="leaderboard-modal-load-more-sentinel"
									/>
								) : null}
							</Box>
						)}
					</Box>
				</DialogContent>
			</Dialog>

			<LeaderboardPointsGuideModal
				open={pointsGuideOpen}
				onClose={() => setPointsGuideOpen(false)}
			/>
		</>
	);
};
